import React, { useEffect, useMemo } from "react";

import { Controller, useForm } from "react-hook-form";
import {
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import { Error } from "../common/Error";
import { Select } from "@ofqual/portal-components/components/Select";
import { AppBarConfirmButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  aoService,
  aoServiceSearch,
  confidencesService,
  intelligenceSourcesService,
  riskTypesService,
  systemicSourcesService,
  tagsService,
  valencesService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { Autocomplete } from "@material-ui/lab";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { authSelector } from "../../redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { RiskAppBar } from "../RiskAppBar";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Delete, Save } from "@material-ui/icons";
import { SubmissionState } from "../../redux/state";
import { SvrSubmission, toApiModel } from "../../services/domain/SvrSubmission";
import { deactivateSubmission, updateSubmission } from "../../redux/actions";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { ItemType } from "@ofqual/portal-components/components/types";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { fieldLabels } from "./fieldLabels";

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 20,
    marginBottom: 30,
  },
  formTitle: {
    color: theme.palette.grey[500],
    fontSize: "1em",
    marginBottom: "15px",
    position: "relative",
    fontWeight: 600,
  },
}));

const URL_PATTERN = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

type UpdateRiskDetailsProps = {
  submissionState: SubmissionState;
};

export const UpdateRiskDetails: React.FC<UpdateRiskDetailsProps> = ({
  submissionState,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    register,
    handleSubmit,
    errors,
    control,
    watch,
    formState,
    reset,
  } = useForm<SvrSubmission>({
    defaultValues: submissionState.submission!,
  });

  useEffect(() => {
    // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
    reset(submissionState.submission!);
  }, [reset, submissionState.submission]);

  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (model: SvrSubmission) => {
    const fields = Object.keys(dirtyFields) as (keyof SvrSubmission)[];
    const document = modelToJsonPatchDocument(model, fields);
    const meta = buildMetaData(
      toApiModel(model),
      submissionState.submission!,
      fields,
      lookupTracker
    );

    dispatch(
      updateSubmission(submissionState.submission!.id, { document, meta })
    );
  };

  const { submissionType } = watch(["submissionType"]);
  const [
    intelligenceSourcesLookups,
    valencesLookups,
    riskTypesLookups,
    confidenceLookups,
    systemicSourcesLookups,
    tagsLookups,
  ] = useLookup(
    {
      service: intelligenceSourcesService,
      id: "id",
      label: "intelligenceSource",
    },
    {
      service: valencesService,
      id: "id",
      label: "valence",
    },
    {
      service: riskTypesService,
      id: "id",
      label: "riskTypeName",
    },
    {
      service: confidencesService,
      id: "id",
      label: "confidence",
    },
    {
      service: systemicSourcesService,
      id: "id",
      label: "tag",
    },
    {
      service: tagsService,
      id: "id",
      label: "tag",
    }
  );

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });

  const { token } = useSelector(authSelector);

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  const isEditable =
    submissionState.status !== "UPDATING" && submissionState.submission?.active;

  return (
    <>
      <RiskAppBar
        breadcrumbs={[
          { label: "Submit observations, intelligence or analysis" },
        ]}
      >
        <AppBarConfirmButton
          tooltip="Deactivate"
          Icon={Delete}
          onClick={() =>
            dispatch(deactivateSubmission(submissionState.submission!.id))
          }
          disabled={!isEditable}
          dataTest="deactivate-submission"
        >
          Are you sure you want to deactivate this submission?
        </AppBarConfirmButton>
        <AppBarSaveButton
          lifecycleLabels={["Save", "Saving", "Saved"]}
          isSubmitting={submissionState.status === "UPDATING"}
          isSubmitted={submissionState.lastCrudResult.isSuccess}
          onClick={handleSubmit(onSubmit)}
          disabled={!isEditable}
          Icon={Save}
          dataTest="save-submission"
        />
      </RiskAppBar>
      <form>
        <div className={classes.formTitle}>
          Do you want to submit an observation or market intelligence about an
          awarding organisation, or about a systemic risk?
        </div>
        <Paper className={classes.formSection}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="submissionType"
                render={({ value, name }) => {
                  return (
                    <RadioGroup
                      aria-label="submission type"
                      name={name}
                      value={value}
                      data-test="svr-level-radio"
                    >
                      <FormControlLabel
                        value={"0"}
                        control={<Radio disabled />}
                        label="Awarding Organisation Risk"
                        data-test="svr-level-radio-ao"
                        checked={value === 0}
                      />
                      <FormControlLabel
                        value={"1"}
                        control={<Radio disabled />}
                        label="Systemic Risk"
                        data-test="svr-level-radio-systemic"
                        checked={value === 1}
                      />
                    </RadioGroup>
                  );
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.formSection}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                name="subject"
                inputRef={register({ required: true, maxLength: 250 })}
                variant="outlined"
                label={fieldLabels.subject}
                fullWidth
                data-test="svr-subject"
                disabled={!isEditable}
              />
              {errors.subject?.type === "required" && (
                <Error>This field is required</Error>
              )}
              {errors.subject?.type === "maxLength" && (
                <Error>Subject must be less than 250 characters</Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                name="description"
                inputRef={register({ required: true, maxLength: 5000 })}
                variant="outlined"
                label={fieldLabels.description}
                fullWidth
                multiline
                rows={6}
                data-test="svr-description"
                disabled={!isEditable}
              />
              {errors.description?.type === "required" && (
                <Error>This field is required</Error>
              )}
              {errors.description?.type === "maxLength" && (
                <Error>Description must be less than 5000 characters</Error>
              )}
            </Grid>
            {submissionType === 0 ? (
              <Grid item xs={4}>
                <Controller
                  control={control}
                  name="intelligenceSourceId"
                  rules={{ required: true }}
                  as={
                    <Select
                      required
                      label={fieldLabels.intelligenceSourceId}
                      data-test="svr-source"
                      disabled={!isEditable}
                      lookupTracker={lookupTracker}
                    >
                      {intelligenceSourcesLookups}
                    </Select>
                  }
                />
                {errors.intelligenceSourceId && (
                  <Error>This field is required</Error>
                )}
              </Grid>
            ) : null}

            <Grid item xs={4}>
              <Controller
                control={control}
                name="valenceId"
                rules={{ required: true }}
                as={
                  <Select
                    required
                    label={fieldLabels.valenceId}
                    data-test="svr-valence"
                    disabled={!isEditable}
                    lookupTracker={lookupTracker}
                  >
                    {valencesLookups}
                  </Select>
                }
              />
              {errors.valenceId && <Error>This field is required</Error>}
            </Grid>

            <Grid item xs={4}>
              <Controller
                control={control}
                name="confidenceId"
                rules={{ required: true }}
                as={
                  <Select
                    required
                    label={fieldLabels.confidenceId}
                    data-test="svr-confidence"
                    disabled={!isEditable}
                    lookupTracker={lookupTracker}
                  >
                    {confidenceLookups}
                  </Select>
                }
              />
              {errors.confidenceId && <Error>This field is required</Error>}
            </Grid>

            {submissionType === 0 ? (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="submissionOrganisations"
                    rules={{ validate: (value) => !!value.length }}
                    as={
                      <SearchField
                        required
                        searchFieldVariant="multiple"
                        label={fieldLabels.submissionOrganisations}
                        datatest="svr-aos"
                        service={mappedOrgLookupService}
                        disabled={!isEditable}
                        lookupTracker={lookupTracker}
                      />
                    }
                  />
                  {errors.submissionOrganisations && (
                    <Error>This field is required</Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="submissionRiskTypes"
                    rules={{
                      validate: (value) => !!value.length,
                    }}
                    as={
                      <Select
                        required
                        selectVariant="multiple"
                        label={fieldLabels.submissionRiskTypes}
                        data-test="svr-riskTypes"
                        disabled={!isEditable}
                        lookupTracker={lookupTracker}
                      >
                        {riskTypesLookups}
                      </Select>
                    }
                  />
                  {errors.submissionRiskTypes && (
                    <Error>This field is required</Error>
                  )}
                </Grid>
              </>
            ) : null}

            <Grid item xs={12}>
              <Controller
                name="submissionLinks"
                rules={{
                  validate: {
                    regex: (value) =>
                      value.every((i: string) => URL_PATTERN.test(i)),
                  },
                }}
                control={control}
                render={({ onChange, ...props }) => (
                  <Autocomplete
                    disabled={!isEditable}
                    multiple
                    autoSelect
                    freeSolo
                    options={[]}
                    ChipProps={{ color: "primary" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="links"
                        variant="outlined"
                        label={fieldLabels.submissionLinks}
                      />
                    )}
                    onChange={(e: any, data: string[]) => onChange(data)}
                    {...props}
                    data-test="svr-links"
                  />
                )}
              />
              {errors.submissionLinks &&
                (errors.submissionLinks as any)?.type === "regex" && (
                  <Error>Links must be valid URls</Error>
                )}
            </Grid>

            {submissionType === 1 ? (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="submissionSources"
                  as={
                    <Select
                      selectVariant="multiple"
                      label={fieldLabels.submissionSources}
                      data-test="svr-sources"
                      disabled={!isEditable}
                      lookupTracker={lookupTracker}
                    >
                      {systemicSourcesLookups}
                    </Select>
                  }
                />
              </Grid>
            ) : null}
          </Grid>
        </Paper>

        <div className={classes.formTitle}>Tags</div>
        <Paper className={classes.formSection}>
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{
                validate: (value) => !!value.length,
              }}
              name="resourceTags"
              as={
                <Select
                  required
                  selectVariant="checkboxes"
                  label={fieldLabels.resourceTags}
                  datatest="tags"
                  disabled={!isEditable}
                  lookupTracker={lookupTracker}
                >
                  {groupedTagsLookup as ItemType[]}
                </Select>
              }
            />
            {errors.resourceTags && <Error>This field is required</Error>}
          </Grid>
        </Paper>
      </form>
    </>
  );
};
